import React, { useState } from 'react';
import { Lightbulb, ArrowRight, Code, Sparkles } from 'lucide-react';

const ProTips = () => {
  const [activeTab, setActiveTab] = useState('structure');

  const commandStructure = [
    {
      pattern: 'Single Action',
      description: 'Execute one command at a time',
      example: 'Open Spotify and play Shape of You'
    },
    {
      pattern: 'Two Actions',
      description: 'Chain two related actions together',
      example: 'Open Chrome and search for the weather today'
    },
    {
      pattern: 'Multiple Actions',
      description: 'Combine several actions in sequence',
      example: 'Open VS Code, maximize window, and set volume to 50'
    }
  ];

  const advancedCommands = {
    title: 'Advanced (In-Dev) Commands',
    warning: 'Twe may crash because these command features are still in Beta',
    pattern: '[ACTION 1], [ACTION 2], then [ACTION 3]',
    description: 'Complex multi-step commands with sequential execution',
    examples: [
      'Open Chrome, search for React, then take screenshot',
      'Launch VS Code, open project folder, then start dev server',
      'Open Spotify, play workout playlist, then set volume to 70'
    ]
  };

  const advancedTips = [
    {
      icon: Sparkles,
      title: 'Use Natural Language',
      description: 'Speak naturally - Twe understands context and intent',
      example: '"Play some focus music while I code" works just as well as specific commands'
    },
    {
      icon: ArrowRight,
      title: 'Chain Multiple Apps',
      description: 'String together 3-5 actions for complex workflows',
      example: 'Launch VS Code, Spotify, Chrome, Discord, then arrange all windows'
    },
    {
      icon: Code,
      title: 'Specify Details',
      description: 'Add parameters like volume levels, window positions, or search terms',
      example: 'Set volume to 30, open Chrome in fullscreen, then search for "AI news"'
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-950/50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <Lightbulb className="w-8 h-8 text-orange-500" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Pro Tips for Chaining Commands
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Master the art of voice automation with these expert patterns
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-zinc-900/50 rounded-lg p-1 border border-white/5">
            <button
              onClick={() => setActiveTab('structure')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'structure'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Command Structure
            </button>
            <button
              onClick={() => setActiveTab('tips')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'tips'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Advanced Tips
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'structure' ? (
          <div>
            {/* Main Pattern Highlight */}
            <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">🎯</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Most Efficient Command Structure
                  </h3>
                  <p className="text-gray-300">
                    Follow this proven pattern for smooth, natural automation
                  </p>
                </div>
              </div>

              <div className="bg-black/40 rounded-lg p-6 border border-orange-500/20 mb-6">
                <code className="text-orange-400 text-xl font-mono">
                  "Action 1" or "Action 2" or "[Action 1], [Action 2]"
                </code>
              </div>

              <div className="space-y-3">
                <div className="bg-zinc-900/50 rounded-lg p-4 border border-white/5">
                  <p className="text-gray-300 font-mono">
                    Example 1: "Open Spotify and play Shape of You"
                  </p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg p-4 border border-white/5">
                  <p className="text-gray-300 font-mono">
                    Example 2: "Open Chrome and search for the weather today"
                  </p>
                </div>
              </div>
            </div>

            {/* Pattern Examples */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {commandStructure.map((item, index) => (
                <div
                  key={index}
                  className="bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-orange-500 font-bold">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {item.pattern}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {item.description}
                  </p>
                  <div className="bg-black/40 rounded-lg p-3 border border-white/5">
                    <p className="text-gray-300 text-sm font-mono">
                      "{item.example}"
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Advanced Commands Warning */}
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-2 border-red-500/30 rounded-2xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-red-500 text-2xl">⚠️</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {advancedCommands.title}
                  </h3>
                  <p className="text-red-400 font-semibold mb-4">
                    {advancedCommands.warning}
                  </p>
                  <div className="bg-black/40 rounded-lg p-4 border border-red-500/20 mb-4">
                    <code className="text-red-400 text-lg font-mono">
                      {advancedCommands.pattern}
                    </code>
                  </div>
                  <p className="text-gray-400 mb-4">{advancedCommands.description}</p>
                  <div className="space-y-2">
                    {advancedCommands.examples.map((example, idx) => (
                      <div key={idx} className="bg-zinc-900/50 rounded-lg p-3 border border-white/5">
                        <p className="text-gray-300 text-sm font-mono">"{example}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {advancedTips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div
                  key={index}
                  className="bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-xl p-8 hover:border-orange-500/50 transition-all duration-300"
                >
                  <div className="flex items-start space-x-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-orange-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-white mb-3">
                        {tip.title}
                      </h3>
                      <p className="text-gray-400 mb-4 text-lg">
                        {tip.description}
                      </p>
                      <div className="bg-black/40 rounded-lg p-4 border border-orange-500/20">
                        <p className="text-gray-300 font-mono text-sm">
                          💡 {tip.example}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProTips;